import React from 'react';
import { createPortal } from 'react-dom';
import styles from './howtoinstall.module.css';
import ShadowTop from '../../components/shadowTop/shadowTop';
import ShadowBottom from '../../components/shadowBottom/shadowBottom';
import CardButton from '../../components/cardButton/CardButton';
import Card from '../../components/card/Card';
import PageBg from '../../components/pageBg/PageBg';

import person from '../../images/cards/person.png';
import sign from '../../images/cards/sign.png';

import { ReactComponent as Steps1 } from '../../images/cards/steps_1.svg';
import { ReactComponent as Steps2 } from '../../images/cards/steps_2.svg';
import { TScrollPage } from '../../utils/types';
import { track, launcherDownloadUrl } from '../../utils/analytics';
import { useSiteConfig } from '../../contexts/SiteConfigContext';
import { useDialog } from '../../hooks/useFocusTrap';
import { useModalPresence } from '../../hooks/useModalPresence';
import modal from '../../components/modal/modalAnim.module.css';

const BASE = process.env.REACT_APP_ADMIN_API_URL ?? '';

/** Сведения о файле, который отдаёт ссылка скачивания (см. /api/links/launcher-build):
 *  инсталлер из installer.json или setup лаунчера из latest.json.
 *  sha256 бэкенд тоже отдаёт, но в окне не показываем: игроки сумму не сверяют,
 *  а блок с хешем и PowerShell делал окно «страшным» */
interface LauncherBuild {
  version: string;
  file:    string;
  size:    number;
}

function fmtMb(bytes: number): string {
  return `${(bytes / 1024 ** 2).toFixed(1)} МБ`;
}

const HowToInstall: React.FC<TScrollPage> = ({reference}) => {
  const [videoReady, setVideoReady] = React.useState(false);
  const targetRef = React.useRef(null);
  const { links } = useSiteConfig();
  const [build, setBuild] = React.useState<LauncherBuild | null>(null);
  // Окно «перед скачиванием»: сначала показываем предупреждение о SmartScreen,
  // скачивание запускает кнопка внутри окна. Раньше клик по карточке сразу
  // навигировал вкладку на редирект-эндпоинт, и окно не успевало показаться —
  // страница уходила прежде, чем игрок видел заметку.
  const [dlOpen, setDlOpen] = React.useState(false);
  // После нажатия «Скачать» окно не закрывается, а показывает «Загрузка началась»
  // и что делать дальше
  const [dlStarted, setDlStarted] = React.useState(false);

  const goToVideoHelp = () => {
    track('outbound_click', 'site', { link: 'help_video' });
    window.open(links.help_video, '_blank');
  }
  const goToBuyArma3 = () => {
    track('outbound_click', 'site', { link: 'arma3_store' });
    window.open(links.arma3_store, '_blank');
  }
  // Шаг 3: аккаунт сайта нужен для входа в лаунчер
  const goToRegister = () => {
    track('outbound_click', 'site', { link: 'register' });
    window.location.href = '/panel/register';
  }
  // Клик по карточке только открывает окно — скачивание ещё не идёт.
  const openDownloadDialog = () => {
    track('download_intent', 'site', { place: 'how_to_install' });
    setDlStarted(false);
    setDlOpen(true);
  }
  const closeDownloadDialog = React.useCallback(() => setDlOpen(false), []);

  // Пунктир «путь игрока» рисуется по штрихам, когда экран появляется:
  // от шага 1 к шагу 2, короткая пауза, затем к шагу 3
  const steps1Ref = React.useRef<SVGSVGElement>(null);
  const steps2Ref = React.useRef<SVGSVGElement>(null);
  React.useEffect(() => {
    const stagger = (svg: SVGSVGElement | null, start: number, duration: number) => {
      if (!svg) return;
      const parts = Array.from(svg.querySelectorAll<SVGElement>('rect, path, circle, ellipse'))
        .filter((el) => !el.closest('defs, filter, clipPath, mask'));
      parts.forEach((el, i) => {
        el.classList.add('dash');
        el.style.setProperty('--sd', `${(start + (i / parts.length) * duration).toFixed(3)}s`);
      });
    };
    stagger(steps1Ref.current, 0.35, 1.6);
    stagger(steps2Ref.current, 2.15, 0.6);
  }, []);
  const dlModal = useModalPresence(dlOpen);
  // Escape, удержание фокуса и возврат его на кнопку скачивания
  const dlDialogRef = React.useRef<HTMLDivElement>(null);
  useDialog(dlDialogRef, closeDownloadDialog, dlOpen);
  // Пока окно открыто, кнопка чата поддержки не должна перекрывать его кнопки
  React.useEffect(() => {
    document.body.classList.toggle('dl-open', dlOpen);
    return () => document.body.classList.remove('dl-open');
  }, [dlOpen]);
  // Скачивание запускает кнопка внутри окна. Открываем в новой вкладке, чтобы
  // текущая с подсказкой про SmartScreen осталась перед глазами: ссылка ведёт
  // на бэкенд, он засчитает скачивание и 302-редиректом отдаст установщик.
  const startDownload = () => {
    track('download_click', 'site', { place: 'how_to_install' });
    window.open(launcherDownloadUrl(), '_blank', 'noopener');
    setDlStarted(true);
  }
  // Сведения о сборке тянем один раз при монтировании: запрос кэшируется на
  // бэкенде и дешевле, чем задержка после клика
  React.useEffect(() => {
    let alive = true;
    fetch(`${BASE}/api/links/launcher-build`)
      .then(r => (r.ok ? r.json() as Promise<{ build: LauncherBuild | null }> : null))
      .then(d => { if (alive && d?.build) setBuild(d.build); })
      .catch(() => { /* сервер раздачи недоступен — покажем окно без строки о файле */ });
    return () => { alive = false; };
  }, []);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const timeout = setTimeout(() => {
            setVideoReady(true);
            clearTimeout(timeout);
          }, 5000);
          observer.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      }
    );

    const target = targetRef.current;
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, []);

  return (
    <PageBg
     type='howtoinstall'
     radial='empty'
     id='howtoinstall'
     reference={reference}
    >
      <ShadowTop />
      <ShadowBottom long />
      <h2 className={styles.title}>Как начать играть</h2>
      <div className={styles.line}></div>
      <img alt='' className={`${styles.person} ${videoReady ? styles.person_active : ''}`} src={person} />
      <button type='button' aria-label='Смотреть видеоинструкцию' className={`${styles.sign} ${styles.signBtn} ${videoReady ? styles.sign_active : ''}`} onClick={goToVideoHelp}><img alt='' src={sign} /></button>
      <div className={styles.cardContainer}>
        <div className={styles.leftContainer}>
          <div className={styles.bgBlue} />
          <Card
            type="blue"
            size="small"
            step="Шаг 1"
            title="ПРИОБРЕСТИ ИГРУ"
            description={<>Купите и установите лицензионную версию Arma 3<span className={styles.cardNote}>Уже есть игра — переходите к шагу 2</span></>}
          >
            <CardButton
              onClick={goToBuyArma3}
              color="blue"
              icon="steam"
              type="button"
              size="medium"
              disable={false}
            >
              Купить
            </CardButton>
          </Card>
        </div>
        <div className={styles.centerContainer}>
          <div className={styles.bgViolet} />
          <Card
            type="violet"
            size="large"
            step="Шаг 2"
            title="СКАЧАТЬ ЛАУНЧЕР"
            description={<>Лаунчер сам установит моды, TeamSpeak 3 с плагином TaskForce и будет их обновлять{build?.version && <span className={styles.cardNote}>Windows · версия {build.version}</span>}</>}
          >
            <CardButton
              onClick={openDownloadDialog}
              color="violet"
              icon="download"
              type="button"
              size="medium"
              disable={false}
            >
              Скачать
            </CardButton>
          </Card>
        </div>
        <div ref={targetRef} className={styles.rightContainer}>
          <div className={styles.bgRed} />
          <Card
            type="red"
            size="small"
            step="Шаг 3"
            title="ВОЙТИ И ИГРАТЬ"
            description={<>Создайте аккаунт и войдите с ним в лаунчер</>}
          >
            <CardButton
              onClick={goToRegister}
              color="red"
              icon="user"
              type="button"
              size="medium"
              disable={false}
            >
              Регистрация
            </CardButton>
          </Card>
        </div>
        <Steps1 ref={steps1Ref} className={`${styles.steps_1} ${styles.stepsDraw}`} aria-hidden="true" />
        <Steps2 ref={steps2Ref} className={`${styles.steps_2} ${styles.stepsDraw}`} aria-hidden="true" />
      </div>

      {/* Сертификата издателя у установщика нет, поэтому предупреждение Windows
          объясняем в окне ДО скачивания. На виду только то, что случается
          почти у всех — SmartScreen; редкий случай «антивирус удалил файл»
          спрятан в спойлер, иначе окно читалось как «страшно, не качай» */}
      {dlModal.mounted && createPortal(
        <div
          className={`${styles.dlOverlay} ${modal.overlay} ${dlModal.closing ? modal.overlayClosing : ''}`}
          onClick={closeDownloadDialog}
        >
          <div
            ref={dlDialogRef}
            tabIndex={-1}
            className={`${styles.dlPanel} ${modal.panel} ${modal.sheet}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="dl-title"
            onClick={e => e.stopPropagation()}
          >
            <span className={styles.dlGrab} aria-hidden="true" />
            <button type="button" className={styles.dlClose} aria-label="Закрыть" onClick={closeDownloadDialog}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
            <div className={styles.dlHead}>
              <span className={styles.dlIcon} aria-hidden="true">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M5 21h14" /></svg>
              </span>
              <div>
                <h3 className={styles.dlTitle} id="dl-title">{dlStarted ? 'Загрузка началась' : 'Скачать AmazingPlatform'}</h3>
                <p className={styles.dlSub}>Установщик для Windows</p>
              </div>
            </div>

            {dlStarted ? (
              <div className={styles.dlDone}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
                <div>
                  <b>{build?.file ?? 'Установщик'}</b> скачивается. Не началась?{' '}
                  <button type="button" className={styles.dlLink} onClick={startDownload}>Скачать ещё раз</button>
                </div>
              </div>
            ) : build && (
              <div className={styles.dlFile}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></svg>
                <div>
                  <b>{build.file}</b>
                  <span>{[build.version && `версия ${build.version}`, build.size > 0 && fmtMb(build.size)].filter(Boolean).join(' · ')}</span>
                </div>
              </div>
            )}

            <p className={styles.dlH}>{dlStarted ? 'Что дальше' : 'Как установить'}</p>
            <ol className={styles.dlSteps}>
              <li><span className={styles.dlN}>1</span><div><b>Запустите установщик.</b> Он скачает актуальную версию AmazingPlatform с серверов проекта, проверит подпись и установит программу.</div></li>
              <li><span className={styles.dlN}>2</span><div><b>Windows может предупредить — это нормально.</b>
                <div className={styles.dlWarn}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /><path d="M12 8v5" /><path d="M12 16h.01" /></svg>
                  <span>Нажмите «Подробнее» → «Выполнить в любом случае».</span>
                </div></div></li>
              <li><span className={styles.dlN}>3</span><div><b>Войдите аккаунтом сайта и привяжите Steam.</b> Мод программа скачает сама, папку для него выберете там же. TeamSpeak и TaskForce тоже поставит она.</div></li>
            </ol>

            <details className={styles.noteMore}>
              <summary className={styles.noteMoreTitle}>Файл пропал после скачивания?</summary>
              <p className={styles.noteHint}>
                Его убрал антивирус — вирусов в файле нет. Откройте
                «Безопасность Windows» → «Журнал защиты» и нажмите «Восстановить».
                Не помогло — напишите в поддержку.
              </p>
            </details>

            <div className={styles.dlActions}>
              <button type='button' className={styles.dlBtnGhost} onClick={closeDownloadDialog}>
                {dlStarted ? 'Готово' : 'Закрыть'}
              </button>
              {!dlStarted && (
                <button type='button' className={styles.dlBtn} onClick={startDownload}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M5 21h14" /></svg>
                  Скачать{build && build.size > 0 ? ` · ${fmtMb(build.size)}` : ''}
                </button>
              )}
            </div>
          </div>
        </div>
      , document.body)}
    </PageBg>
  );
}

export default HowToInstall;